import { cx } from './ui'

/**
 * The task key, treated as the task's real name.
 *
 * This team pastes SHOP-142 into Slack messages and commit subjects all day,
 * so the key is not a muted reference number tucked beside the title — it is
 * set in the platform monospace, letter-spaced, with the project prefix
 * stepped back so the number reads first. Stacked down a column, the aligned
 * keys form a rail that lets you scan a board the way you scan a log.
 *
 * In the detail drawer the key becomes the heading and the title sits beneath
 * it. That inverts the usual hierarchy on purpose: the key is the handle
 * people actually communicate in.
 */
const SIZES = {
  card: 'text-[12px]',
  inline: 'text-[12.5px]',
  heading: 'text-[22px] leading-none tracking-[0.01em]',
} as const

export function TaskKey({
  taskKey,
  size = 'card',
  className,
}: {
  taskKey: string
  size?: keyof typeof SIZES
  className?: string
}) {
  // Split on the last dash: project keys never contain one, but being explicit
  // costs nothing and survives a future format change.
  const dash = taskKey.lastIndexOf('-')
  const prefix = dash > 0 ? taskKey.slice(0, dash) : ''
  const number = dash > 0 ? taskKey.slice(dash + 1) : taskKey

  return (
    <span className={cx('task-key font-medium', SIZES[size], className)}>
      {prefix && <span className="task-key-prefix">{prefix}-</span>}
      {number}
    </span>
  )
}
